import { useState } from "react";
import { useOutletContext } from "react-router";
import {
  MODULE_DEFINITIONS,
  MODULE_SLUGS,
  MODULE_STATUSES,
  type ModuleSlug,
  type ModuleStatus,
} from "@a2/core";
import type { SessionUser } from "../../lib/session-loader";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import { AdminLayout } from "../../components/admin/AdminLayout";

export function AdminModulesPage() {
  const t = useT();
  const session = useOutletContext<SessionUser>();
  const [statusMap, setStatusMap] = useState(session.moduleStatusMap);
  const [pending, setPending] = useState<ModuleSlug | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function setStatus(slug: ModuleSlug, next: ModuleStatus) {
    if (statusMap[slug] === next) return;
    setPending(slug);
    setError(null);
    try {
      const res = await authedFetch(`/admin/modules/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatusMap({ ...statusMap, [slug]: next });
    } catch (e) {
      setError(t("admin.modules.updateFailedFmt", { module: slug, error: (e as Error).message }));
    } finally {
      setPending(null);
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="space-y-1">
          <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
            {t("admin.sidebar.group.system")}
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {t("admin.modules.title")}
          </h1>
        </div>
        <p className="text-sm text-[var(--ink-muted)]">
          {t("admin.modules.description")}
        </p>
        {error ? (
          <div className="rounded-md border border-[var(--fail)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--fail)]">
            {error}
          </div>
        ) : null}
        <ul className="space-y-2">
          {MODULE_SLUGS.map((slug) => {
            const def = MODULE_DEFINITIONS[slug];
            const status = statusMap[slug];
            const isPending = pending === slug;
            return (
              <li
                key={slug}
                className="rounded-md border border-[var(--border)] bg-[var(--panel)] px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="font-semibold">{def.label}</div>
                  <div className="text-xs text-[var(--ink-faint)] font-mono">{def.route}</div>
                  <div className="mt-1 text-xs text-[var(--ink-muted)]">
                    {status === "active"
                      ? t("admin.modules.statusHelpActive")
                      : status === "binnenkort"
                        ? t("admin.modules.statusHelpSoon")
                        : t("admin.modules.statusHelpHidden")}
                  </div>
                </div>
                <fieldset
                  className="shrink-0"
                  aria-label={t("admin.modules.statusAriaLabelFmt", { module: def.label })}
                  disabled={isPending}
                >
                  <ul className="inline-flex items-stretch rounded-full border border-[var(--border)] bg-[var(--panel-soft)] p-1 text-xs">
                    {MODULE_STATUSES.map((s) => {
                      const selected = status === s;
                      return (
                        <li key={s}>
                          <button
                            type="button"
                            onClick={() => setStatus(slug, s)}
                            aria-pressed={selected}
                            className={
                              selected
                                ? "inline-flex h-8 items-center rounded-full bg-[var(--accent)] px-3 font-semibold text-[var(--bg)] transition-colors"
                                : "inline-flex h-8 items-center rounded-full px-3 text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--accent-soft)] transition-colors disabled:opacity-50"
                            }
                          >
                            {s === "active"
                              ? t("admin.modules.statusActive")
                              : s === "binnenkort"
                                ? t("admin.modules.statusSoon")
                                : t("admin.modules.statusHidden")}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
              </li>
            );
          })}
        </ul>
      </div>
    </AdminLayout>
  );
}
